import { Autocomplete } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { Civic } from 'shell/models/civic';
import { HttpResponseList } from 'shell/models/http-response';
import debounce from 'shell/utils/debounce';
import PmiTextField from 'shell/components/shared/text-field';
import { transformExponent } from './transform-exponent';

export interface StreetNumberVM {
  value: string;
  id: number | string;
  cap?: string;
  exponent?: string;
  tipoAbitazione?: 'A' | 'C';
}

interface StreetNumberListProps {
  disabled?: boolean;
  valueStreetNumber: StreetNumberVM | null;
  onChange: (value: StreetNumberVM | null) => void;
  error?: boolean;
  helperText?: string;
  circulationArea?: string;
  label?: string;
  showExponent?: boolean;
  noEnded?: boolean;
}

const StreetNumberListComponent = ({
  disabled,
  onChange,
  valueStreetNumber,
  error,
  circulationArea,
  label,
  helperText,
  showExponent,
  noEnded,
}: StreetNumberListProps): React.ReactElement => {
  const {
    get: getCivicList,
    data: civicM,
    error: civicError,
    loading: loadingCivics,
  } = useHttpFetch<HttpResponseList<Civic>>(
    `${__TOPONYMY_API_URL__}/areaCircolazione/${circulationArea}`
  );

  const handleSearch = debounce<string>(
    useCallback(
      (valueSearch) => {
        getCivicList(`/civico?numeroCivico=${valueSearch}${noEnded ? '&nonCessati=1' : ''}`);
      },
      [getCivicList, noEnded]
    )
  );

  const civics: Array<StreetNumberVM> = useMemo(() => {
    if (!!civicError || !civicM?.payload) {
      return [];
    }
    return civicM.payload.list.map((civic) => ({
      value: showExponent
        ? `${civic.key?.numero.toString()}${transformExponent(civic.key?.esponente || '') || ''}`
        : civic.key?.numero.toString(),
      id: civic.key.numero,
      cap: `${civic.cap}`,
      exponent: civic.key?.esponente,
      tipoAbitazione: civic.tipologiaAbitazione as 'A' | 'C',
    }));
  }, [civicM, civicError]);

  return (
    <Autocomplete
      options={civics}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      getOptionLabel={(option: StreetNumberVM) => option.value}
      id="streetNumber"
      value={valueStreetNumber || null}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element as StreetNumberVM);
      }}
      onInputChange={(event, value, reason) => {
        if (!value) {
          return;
        }
        if (reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={loadingCivics}
      disabled={disabled}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={label}
          id={`${label}-Autocomplete`}
          name={label}
          fullWidth
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

StreetNumberListComponent.displayName = 'Street Number List component';

const StreetNumberList = memo(StreetNumberListComponent);

export default StreetNumberList as typeof StreetNumberListComponent;
